import React from "react";
import { View, StyleSheet, TouchableOpacity, TextInput } from "react-native";
import {
  InputPatientDetail,
  NormalText,
  SecondaryButton,
} from "../../../../../common/native";
import {
  BoldFont,
  ItalicFont,
  TextAlignLeft,
  TextAlignRight,
  TextAlignCenter,
  UnderlineFont,
  TextWrap,
} from "../../../../../../assets";

type FormattingContentProps = {
  textContent: string;
  setTextContent: (value: string) => void;
  fontSize: number;
  setFontSize: (value: number) => void;
  formats: {
    bold: boolean;
    italic: boolean;
    underline: boolean;
  };
  setFormats: (value: {
    bold: boolean;
    italic: boolean;
    underline: boolean;
  }) => void;
  textAlignment: "left" | "center" | "right";
  setTextAlignment: (value: "left" | "center" | "right") => void;
  textWrap: "nowrap" | "wrap";
  setTextWrap: (value: "nowrap" | "wrap") => void;
  textWidth: number | "initial";
  setTextWidth: (value: number | "initial") => void;
  autoFocusText?: boolean;
  showDeleteText?: boolean;
  onDeleteText?: () => void;
};

const Row = ({
  label,
  children,
  rowStyle,
}: {
  label: string;
  children: React.ReactNode;
  rowStyle?: any;
}) => (
  <View style={[styles.row, rowStyle]}>
    <View style={styles.labelContainer}>
      <NormalText text={label} style={styles.label} />
    </View>
    <View style={styles.rowContent}>{children}</View>
  </View>
);

const FormattingContent: React.FC<FormattingContentProps> = ({
  textContent,
  setTextContent,
  fontSize,
  setFontSize,
  formats,
  setFormats,
  textAlignment,
  setTextAlignment,
  textWrap,
  setTextWrap,
  textWidth,
  setTextWidth,
  autoFocusText = false,
  showDeleteText = false,
  onDeleteText,
}) => {
  const [fontSizeInput, setFontSizeInput] = React.useState(`${fontSize}`);
  const [textWidthInput, setTextWidthInput] = React.useState(
    textWidth === "initial" ? "" : `${textWidth}`,
  );
  const textInputRef = React.useRef<TextInput>(null);

  React.useEffect(() => {
    setFontSizeInput(`${fontSize}`);
  }, [fontSize]);

  React.useEffect(() => {
    setTextWidthInput(textWidth === "initial" ? "" : `${textWidth}`);
  }, [textWidth]);

  React.useEffect(() => {
    if (!autoFocusText) return;
    const timeout = setTimeout(() => {
      textInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timeout);
  }, [autoFocusText]);

  const toggleFormat = (format: keyof typeof formats) => {
    const updatedFormats = { ...formats, [format]: !formats[format] };
    setFormats(updatedFormats);
  };

  return (
    <View style={styles.container}>
      {showDeleteText && onDeleteText && (
        <View style={styles.deleteButtonRow}>
          <SecondaryButton
            text="Remove textbox"
            onPress={onDeleteText}
            // width={140}
            fullWidth={true}
            style={styles.deleteButton}
            textStyle={styles.deleteButtonText}
          />
        </View>
      )}
      <Row label="Text Content">
        <InputPatientDetail
          ref={textInputRef}
          name=""
          value={textContent}
          inputHeight={60}
          multiline={true}
          numberOfLines={3}
          maxLength={500}
          labelStyle={styles.inputLabel}
          style={[styles.inputCompact, styles.textContentInput]}
          onChangeText={setTextContent}
          selectTextOnFocus={textContent === "Your text here"}
        />
      </Row>

      <Row label="Text Width" rowStyle={styles.rowStackSpacing}>
        <InputPatientDetail
          name=""
          value={textWidthInput}
          textWidth={120}
          type="numeric"
          placeholder="initial"
          labelStyle={styles.inputLabel}
          inputHeight={28}
          style={[styles.inputCompact, styles.fontSizeInput]}
          onChangeText={(value) => {
            setTextWidthInput(value);
            if (value.trim() === "") {
              setTextWidth("initial");
              return;
            }
            const parsed = parseInt(value, 10);
            if (!Number.isNaN(parsed)) {
              setTextWidth(parsed);
            }
          }}
          onBlur={() => {
            if (textWidthInput.trim() === "") {
              setTextWidth("initial");
              return;
            }
            const parsed = parseInt(textWidthInput, 10);
            if (!Number.isNaN(parsed)) {
              setTextWidthInput(`${parsed}`);
            }
          }}
        />
      </Row>
      <Row label="Font Size">
        <InputPatientDetail
          name=""
          value={fontSizeInput}
          textWidth={120}
          inputHeight={28}
          type="numeric"
          placeholder="Custom"
          style={styles.fontSizeInput}
          labelStyle={styles.inputLabel}
          onChangeText={(value) => {
            setFontSizeInput(value);
            const parsed = parseInt(value, 10);
            if (!Number.isNaN(parsed)) {
              setFontSize(parsed);
            }
          }}
          onBlur={() => {
            if (fontSizeInput.trim() === "") {
              setFontSizeInput(`${fontSize}`);
            }
          }}
        />
        <TouchableOpacity
          style={[
            styles.formatButton,
            styles.textWrapButton,
            textWrap === "wrap" && styles.activeFormatButton,
          ]}
          onPress={() =>
            // @ts-ignore
            setTextWrap((prev) => (prev === "nowrap" ? "wrap" : "nowrap"))
          }
        >
          <TextWrap />
        </TouchableOpacity>
      </Row>

      <Row label="Alignment">
        <View style={styles.formatButtons}>
          <TouchableOpacity
            style={[
              styles.formatButton,
              textAlignment === "left" && styles.activeFormatButton,
            ]}
            onPress={() => setTextAlignment("left")}
          >
            <TextAlignLeft />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.formatButton,
              textAlignment === "center" && styles.activeFormatButton,
            ]}
            onPress={() => setTextAlignment("center")}
          >
            <TextAlignCenter />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.formatButton,
              textAlignment === "right" && styles.activeFormatButton,
            ]}
            onPress={() => setTextAlignment("right")}
          >
            <TextAlignRight />
          </TouchableOpacity>
        </View>
      </Row>

      <Row label="Format">
        <View style={styles.formatButtons}>
          <TouchableOpacity
            style={[
              styles.formatButton,
              formats.bold && styles.activeFormatButton,
            ]}
            onPress={() => toggleFormat("bold")}
          >
            <BoldFont />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.formatButton,
              formats.italic && styles.activeFormatButton,
            ]}
            onPress={() => toggleFormat("italic")}
          >
            <ItalicFont />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.formatButton,
              formats.underline && styles.activeFormatButton,
            ]}
            onPress={() => toggleFormat("underline")}
          >
            <UnderlineFont />
          </TouchableOpacity>
        </View>
      </Row>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // flex: 1,
    // gap: 10,
    // width: "100%",
    // maxWidth: 350,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: 6,
    width: "100%",
    maxWidth: 280,
    gap: 8,
  },
  labelContainer: {
    width: 80,
  },

  label: {
    fontFamily: "Urbanist500",
    fontWeight: "500",
    fontSize: 12,
    color: "#282B33",
  },
  inputLabel: {
    fontSize: 12,
  },
  formatButtons: {
    flexDirection: "row",
    gap: 6,
    flexShrink: 0,
  },
  formatButton: {
    width: 26,
    height: 26,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#C4C6CD",
    borderRadius: 4,
    backgroundColor: "#F8F8F8",
  },
  textWrapButton: {
    width: 24,
    height: 24,
  },
  fontSizeInput: {
    width: 60,
    fontSize: 12,
    paddingVertical: 4,
  },
  inputCompact: {
    fontSize: 12,
    paddingVertical: 4,
  },
  rowContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    zIndex: 10,
    minWidth: 0,
  },
  rowStackSpacing: {
    marginBottom: 4,
  },
  textContentInput: {
    width: 160,
  },
  deleteButtonRow: {
    width: "100%",
    maxWidth: 280,
    alignItems: "flex-start",
    marginTop: -2,
    marginBottom: 6,
  },
  deleteButton: {
    alignSelf: "flex-start",
    height: 30,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  deleteButtonText: {
    fontSize: 12,
    lineHeight: 16,
  },
  activeFormatButton: {
    backgroundColor: "#DDDEE0", // Highlight active button
  },
});

export default FormattingContent;
